import { graphql } from 'react-relay';
import { useContext } from 'react';
import {
	type SearchExperimentLayer,
	type BackendExperiment,
	type ExperimentLayer,
} from '@atlassian/search-experiment';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { perfMarkStart } from '@confluence/performance/entry-points/performance.ts';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { useIsWhiteboardFeatureEnabled } from '@confluence/whiteboard-utils/entry-points/useIsWhiteboardFeatureEnabled.ts';
import { ADVANCED_SEARCH_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useDynamicBackendExperiment } from '@confluence/navigation-search-dialog/entry-points/useDynamicBackendExperiment.ts';

import {
	getQueryVariablesForCQLSearch,
	getQueryVariablesForAggSearch,
	getEntities,
} from './utils/gqlQueryVariables.ts';
import { PAGE_SIZE, CONFLUENCE_ARI } from './AdvancedSearch-constants.ts';
import type {
	useResultSupplierAdvancedSearchCQLQuery as useResultSupplierAdvancedSearchCQLQuery$data,
	useResultSupplierAdvancedSearchCQLQueryVariables as useResultSupplierAdvancedSearchCQLQuery$variables,
} from './__types__/useResultSupplierAdvancedSearchCQLQuery.ts';
import type {
	useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_abTest_SearchAbTest,
	useResultSupplierAdvancedAGGSearchQuery as useResultSupplierAdvancedAGGSearchQuery$data,
	useResultSupplierAdvancedAGGSearchQueryVariables as useResultSupplierAdvancedAGGSearchQuery$variables,
} from './__types__/useResultSupplierAdvancedAGGSearchQuery.ts';
import { cqlDataMapper, aggDataMapper } from './utils/result-mapper.ts';
import type {
	AdvancedSearchResultType,
	FilterStateType,
	QueryType,
} from './AdvancedSearch-types.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

/**
 * This error can be reproduced when a user is searching in a stale container & aren't authenticated.
 * To reproduce, open two tabs on Advanced Search. Log out of confluence from one of the tabs, and search in the other.
 * You should get a network call with a 200 status code and an error message 403 forbidden.
 * It is thrown by api_search intentionally and counted as user errors, and thus is expected and should not affect our reliability metrics.
 */
const isExpected403Error = (error: Error): boolean =>
	error.message.includes('Problem while calling endpoint api_search: HTTP 403 Forbidden');

const loadMoreData = (fetchMore, after, aggSearchQueryEnabled = false) => {
	perfMarkStart({
		subject: 'advancedSearchLoadMoreResult',
	});

	fetchMore({
		variables: {
			first: PAGE_SIZE,
			after,
		},
		updateQuery: (prev, { fetchMoreResult }) => {
			if (aggSearchQueryEnabled) {
				const prevAGGResults = prev?.search?.search?.edges || [];
				const nextAGGResults = fetchMoreResult?.search?.search?.edges || [];
				return {
					search: {
						__typename: prev.search.__typename,
						search: {
							...fetchMoreResult.search.search,
							edges: [...prevAGGResults, ...nextAGGResults],
						},
					},
				};
			}

			const prevResults = prev?.search?.nodes || [];
			const nextResults = fetchMoreResult?.search?.nodes || [];
			return {
				search: {
					...fetchMoreResult.search,
					nodes: [...prevResults, ...nextResults],
				},
			};
		},
	});
};

const isCustomerAccountError = (error) =>
	error.message.includes('Service search does not support customer accounts');

type useResultSupplierType = (args: {
	queryType: QueryType;
	searchQueryText: string;
	cql: string | string[] | undefined;
	searchFilters: FilterStateType;
	searchSessionId: string;
	sessionHasUserInput: boolean;
}) => {
	results: AdvancedSearchResultType[];
	loading: boolean;
	error: ApolloErrorInterop | any;
	loadMore: () => void;
	hasNextPage: boolean;
	totalCount?: number | null;
	numResults: number;
	abTest:
		| useResultSupplierAdvancedAGGSearchQuery_search_SearchQueryAPI_search_SearchItemConnection_abTest_SearchAbTest
		| BackendExperiment
		| null;
	experimentLayers?: SearchExperimentLayer[];
};

export const useResultSupplier: useResultSupplierType = ({
	queryType,
	searchQueryText,
	cql,
	searchFilters,
	searchSessionId,
	sessionHasUserInput,
}) => {
	const { isWhiteboardFeatureEnabled } = useIsWhiteboardFeatureEnabled();
	const isWhiteboardsEnabledAS = isWhiteboardFeatureEnabled('whiteboardsEnabled');
	const isDatabasesEnabledAS = isContentTypeEnabledInCurrentEnv('database');
	const { cloudId, activationId, userId } = useSessionData();
	const confluenceInstance = CONFLUENCE_ARI.concat(cloudId);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const {
		data: cqlData,
		loading: cqlLoading,
		error: cqlError,
		fetchMore: cqlFetchMore,
	} = useQueryInterop<
		useResultSupplierAdvancedSearchCQLQuery$data,
		useResultSupplierAdvancedSearchCQLQuery$variables
	>(
		/** @schema cc */ graphql`
			query useResultSupplierAdvancedSearchCQLQuery($cql: String!, $first: Int, $after: String) {
				search: confluence_search(cql: $cql, first: $first, after: $after) {
					nodes {
						entityType
						title
						excerpt
						url
						lastModified
						content {
							id
							type
							ancestors {
								id
								title
								links {
									webUi: webui
								}
							}
							space {
								id
								key
								name
								links {
									webUi: webui
								}
								status
							}
							extensions {
								key
								value
							}
						}
						space {
							id
							name
							type
							icon {
								path
							}
						}
					}
					pageInfo {
						hasNextPage
						endCursor
					}
				}
			}
		`,
		{
			...getQueryVariablesForCQLSearch(cql ?? ''),
			skip: queryType !== 'CQLQuery',
			notifyOnNetworkStatusChange: true,
			errorPolicy: 'all',
		},
	);

	const entities = getEntities({
		filters: searchFilters,
		isWhiteboardsEnabled: isWhiteboardsEnabledAS,
		isDatabasesEnabled: isDatabasesEnabledAS,
	});

	const {
		backendExperiment: dynamicBackendExperiment,
		loading: dynamicBackendExperimentLoading,
		productEdition,
	} = useDynamicBackendExperiment({
		cloudId,
		entities,
		userId: userId || undefined,
		experience: 'advanced-search',
	}) || {};

	const backendExperiment = dynamicBackendExperiment;

	const {
		data: aggData,
		loading: aggLoading,
		error: aggError,
		fetchMore: aggFetchMore,
	} = useQueryInterop<
		useResultSupplierAdvancedAGGSearchQuery$data,
		useResultSupplierAdvancedAGGSearchQuery$variables
	>(
		/** @schema agg */ graphql`
			query useResultSupplierAdvancedAGGSearchQuery(
				$experience: String!
				$analytics: SearchAnalyticsInput
				$query: String!
				$first: Int
				$after: String
				$filters: SearchFilterInput!
				$experimentContext: SearchExperimentContextInput
				$isLivePagesEnabled: Boolean!
			) {
				search {
					search(
						experience: $experience
						analytics: $analytics
						query: $query
						first: $first
						after: $after
						filters: $filters
						experimentContext: $experimentContext
					) {
						edges {
							node {
								id
								title
								type
								url
								... on SearchConfluencePageBlogAttachment {
									entityId
									excerpt
									isVerified
									lastModified
									space {
										id
										spaceId
										name
										key
										webUiLink
									}
									confluenceEntity {
										... on ConfluencePage {
											id
											type
											subtype @include(if: $isLivePagesEnabled)
											ancestors {
												title
												id
												links {
													webUi
												}
											}
											metadata {
												titleEmojiPublished {
													id
													key
													value
												}
											}
										}
									}
								}
								... on SearchConfluenceSpace {
									entityId
									key
									iconPath
									webUiLink
								}
							}
						}
						pageInfo {
							endCursor
						}
						abTest {
							abTestId
							controlId
							experimentId
						}
						totalCount
					}
				}
			}
		`,
		{
			...getQueryVariablesForAggSearch({
				searchQueryText,
				selectedFilters: searchFilters,
				confluenceInstance,
				searchSessionId,
				activationId,
				userId,
				isWhiteboardsEnabled: isWhiteboardsEnabledAS,
				isDatabasesEnabled: isDatabasesEnabledAS,
				isLivePagesEnabled: fg('confluence_live_pages_open_beta_trait_opted_in'),
				experimentId: backendExperiment?.experimentId,
				experimentLayers: backendExperiment?.experimentLayers?.map(
					({ name, layerId, shadowId, definitions }: ExperimentLayer | SearchExperimentLayer) => ({
						name,
						layerId,
						shadowId,
						definitions,
					}),
				),
			}),
			skip: Boolean(
				queryType !== 'AGGQuery' || !sessionHasUserInput || dynamicBackendExperimentLoading,
			),
			notifyOnNetworkStatusChange: true,
			errorPolicy: 'all',
			client: getAGGClient(),
		},
	);

	if (aggError) {
		if (
			isCustomerAccountError(aggError) ||
			isStatusCodeError(aggError, '401') ||
			isExpected403Error(aggError)
		) {
			markErrorAsHandled(aggError);
			experienceTracker.abort({
				name: ADVANCED_SEARCH_EXPERIENCE,
				reason: 'user not authorized',
			});
		} else {
			experienceTracker.stopOnError({
				name: ADVANCED_SEARCH_EXPERIENCE,
				error: aggError,
			});
		}
	}

	return queryType === 'CQLQuery'
		? {
				results: cqlDataMapper(cqlData),
				loading: cqlLoading,
				error: cqlError,
				loadMore: () => loadMoreData(cqlFetchMore, cqlData?.search?.pageInfo?.endCursor || ''),
				hasNextPage: cqlData?.search?.pageInfo?.hasNextPage || false,
				numResults: cqlDataMapper(cqlData).length || 0,
				abTest: null,
			}
		: {
				results: aggDataMapper(aggData),
				loading: aggLoading,
				error: aggError,
				loadMore: () =>
					loadMoreData(aggFetchMore, aggData?.search?.search?.pageInfo?.endCursor || '', true),
				hasNextPage: !!aggData?.search?.search?.pageInfo?.endCursor,
				totalCount: aggData?.search?.search?.totalCount,
				numResults: aggDataMapper(aggData).length || 0,
				abTest: null,
				experimentLayers: backendExperiment?.experimentLayers?.map((layer) => {
					const { analyticsDefinitions, definitions: _definitions, ...rest } = layer;

					return {
						...rest,
						definitions: analyticsDefinitions,
					};
				}),
				productEdition,
			};
};
